import { flushSync } from "react-dom";
import type { QueuedToast, ToastOptions } from "react-aria-components";
import {
  UNSTABLE_ToastQueue as ToastQueue,
  UNSTABLE_ToastRegion as ToastRegion,
} from "react-aria-components";

import { cn } from "@/lib/helpers/cn";
import { DEFAULT_TOAST_TIMEOUT } from "@/lib/constants/delay";
import { Toast2 } from "@/ui2/molecules/Toast";
import type { ToastConfig as ToastContentType } from "@/ui2/molecules/Toast";

export type Toast = QueuedToast<ToastContentType>;
type ExtendedToastOptions = ToastOptions & {
  /** Delay in milliseconds before the toast is shown. */
  delay?: number;
};

export type ToastParams = [ToastContentType, ExtendedToastOptions?];

// Guards against nested startViewTransition calls. React Aria's ToastQueue
// calls wrapUpdate to notify React subscribers, but it mutates visibleToasts
// *before* invoking wrapUpdate — meaning by the time startViewTransition
// captures the "old" DOM snapshot, the toast is already gone from the store.
//
// The fix: closeToast() wraps the entire queue.close() call (mutation included)
// inside startViewTransition so the old snapshot is captured before any state
// change. wrapUpdate then fires a second time for the subscriber notification
// — this flag short-circuits that inner call to a plain fn() to avoid nesting.
let isWrappingUpdate = false;

function withToastTransition(fn: () => void) {
  if (!("startViewTransition" in document) || isWrappingUpdate) {
    fn();

    return;
  }
  isWrappingUpdate = true;
  document
    .startViewTransition(() => {
      flushSync(fn);
    })
    .finished.finally(() => {
      isWrappingUpdate = false;
    });
}

export const toastProviderQueue = new ToastQueue<ToastContentType>({
  wrapUpdate: withToastTransition,
});

// Use this instead of toastProviderQueue.close() directly. Wrapping the full
// close() call in withToastTransition ensures the DOM still shows the toast
// when the "old" snapshot is taken, giving the slide-out animation its target.
export function closeToast(key: string) {
  withToastTransition(() => toastProviderQueue.close(key));
}

export function showToast([content, options]: ToastParams) {
  const { delay, ...ariaOptions } = options ?? {};
  const add = () =>
    toastProviderQueue.add(content, {
      timeout: DEFAULT_TOAST_TIMEOUT,
      ...ariaOptions,
    });

  if (delay) return void setTimeout(add, delay);

  return add();
}

export function ToastProvider({ children }: { children: React.ReactNode }) {
  return (
    <>
      <ToastRegion
        className={cn(
          "react-aria-ToastRegion",
          "tw:fixed tw:top-[calc(var(--header-height)+20px)] tw:right-5 tw:z-overlay",
        )}
        queue={toastProviderQueue}
      >
        {({ toast }) => (
          <Toast2
            onClose={() => closeToast(toast.key)}
            style={{ viewTransitionName: toast.key }}
            toast={toast}
          />
        )}
      </ToastRegion>

      {children}
    </>
  );
}
